import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invoicesAPI, currenciesAPI, vendorsAPI, purchaseOrdersAPI } from '../api/client';
import apiClient from '../api/client';
import { AlertTriangle, Trash2, ChevronLeft, ChevronRight, FileText, Scissors, Undo2, GitBranch, CheckCircle, XCircle, Clock, Copy, RefreshCw, Pencil, PlusCircle, X } from 'lucide-react';
import SplitInvoiceModal from '../components/SplitInvoiceModal';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { isDepartmentManager } from '../utils/roleHelpers';
import { useModules } from '../modules/useModules';
import CurrencyDisplay, { getCurrencySymbol as getSymbol } from '../components/CurrencyDisplay';
import DeleteInvoiceModal from '../components/DeleteInvoiceModal';
import InvoiceApprovalDetail from '../components/InvoiceApprovalDetail';
import DocumentProgressTimeline from '../components/DocumentProgressTimeline';
import SkeletonLoader from '../components/SkeletonLoader';
import { useUnsavedChanges } from '../utils/useUnsavedChanges';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { isEnabled: isModuleEnabled } = useModules();
  const isDeptManager = isDepartmentManager(user);
  const [loading, setLoading] = useState(true);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [showDocument, setShowDocument] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // GL assignment + submit modal for Draft/New invoices
  const [showGLAssignModal, setShowGLAssignModal] = useState(false);
  
  // Split state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitChildren, setSplitChildren] = useState([]);
  const [splitInfo, setSplitInfo] = useState(null);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [editedLines, setEditedLines] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  useUnsavedChanges(hasChanges); // blocks browser close/refresh when unsaved changes exist
  const [saving, setSaving] = useState(false);
  const [vendorError, setVendorError] = useState(null); // { message, vendorStatus, vendorName }

  // Quick-add vendor modal
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [addVendorForm, setAddVendorForm] = useState({ vendorName: '', contactEmail: '', contactPhone: '' });
  const [addVendorSaving, setAddVendorSaving] = useState(false);
  const [addVendorError, setAddVendorError] = useState('');

  const handleQuickAddVendor = async (e) => {
    e.preventDefault();
    if (!addVendorForm.vendorName.trim()) { setAddVendorError('Vendor name is required'); return; }
    setAddVendorSaving(true);
    setAddVendorError('');
    try {
      const res = await apiClient.post('/vendors', addVendorForm);
      const newVendorId = res.data.vendorId;
      // Refresh vendor list then auto-select
      await fetchVendors();
      handleFieldChange('invoice', 'VendorID', newVendorId);
      setShowAddVendorModal(false);
      setAddVendorForm({ vendorName: '', contactEmail: '', contactPhone: '' });
    } catch (err) {
      setAddVendorError(err.response?.data?.message || 'Failed to create vendor');
    } finally {
      setAddVendorSaving(false);
    }
  };
  
  // Duplicate navigation states
  const [duplicates, setDuplicates] = useState([]);
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0);
  
  // Applied credits (for regular invoices that have credit notes against them)
  const [appliedCredits, setAppliedCredits] = useState(null); // null = not loaded
  
  // Currency recalculation state
  const [showRecalcPanel, setShowRecalcPanel] = useState(false);
  const [recalcRate, setRecalcRate] = useState('');
  const [recalculating, setRecalculating] = useState(false);

  // ERP payment status sync
  const [syncingPayment, setSyncingPayment] = useState(false);
  const [erpPayment, setErpPayment] = useState(null); // overrides invoice fields after sync
  const [editingPayment, setEditingPayment] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ ERPPaymentStatus: '', ERPPaidAt: '', ERPPaidAmount: '', ERPPaymentRef: '' });

  const iframeRef = useRef(null);
  const selectedFieldInputRef = useRef(null);

  const handleSyncPaymentStatus = async () => {
    if (!id || syncingPayment) return;
    setSyncingPayment(true);
    try {
      const res = await apiClient.post(`/invoice-posting/sync-payment/${id}`);
      setErpPayment(res.data);
    } catch (err) {
      console.error('Sync payment status error:', err);
    }
    setSyncingPayment(false);
  };

  const handleEditPaymentClick = () => {
    const current = {
      ERPPaymentStatus: erpPayment?.ERPPaymentStatus ?? invoice?.ERPPaymentStatus ?? '',
      ERPPaidAt:        erpPayment?.ERPPaidAt ?? invoice?.ERPPaidAt
                          ? (erpPayment?.ERPPaidAt ?? invoice?.ERPPaidAt).slice(0, 10)
                          : '',
      ERPPaidAmount:    erpPayment?.ERPPaidAmount ?? invoice?.ERPPaidAmount ?? '',
      ERPPaymentRef:    erpPayment?.ERPPaymentRef ?? invoice?.ERPPaymentRef ?? '',
    };
    setPaymentForm(current);
    setEditingPayment(true);
  };

  const handleSavePaymentStatus = async () => {
    if (savingPayment) return;
    setSavingPayment(true);
    try {
      const body = {
        ERPPaymentStatus: paymentForm.ERPPaymentStatus || null,
        ERPPaidAt:        paymentForm.ERPPaidAt || null,
        ERPPaidAmount:    paymentForm.ERPPaidAmount !== '' ? parseFloat(paymentForm.ERPPaidAmount) : null,
        ERPPaymentRef:    paymentForm.ERPPaymentRef || null,
      };
      const res = await apiClient.patch(`/invoices/${id}/payment-status`, body);
      setErpPayment(res.data);
      setEditingPayment(false);
    } catch (err) {
      console.error('Save payment status error:', err);
    }
    setSavingPayment(false);
  };

  useEffect(() => {
    fetchInvoice();
    fetchCurrencies();
    fetchVendors();
    fetchDepartments();
    fetchSplitChildren();
  }, [id]);

  useEffect(() => {
    // Load applied credits once invoice is loaded and it's not itself a credit note
    if (invoice && invoice.DocumentType !== 'CreditNote') {
      apiClient.get(`/invoices/${invoice.InvoiceID}/applied-credits`)
        .then(r => setAppliedCredits(r.data))
        .catch(() => setAppliedCredits(null));
    }
  }, [invoice?.InvoiceID, invoice?.DocumentType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Focus the selected field input when a field is selected
    if (selectedField && selectedFieldInputRef.current) {
      selectedFieldInputRef.current.focus();
    }
  }, [selectedField]);

  useEffect(() => {
    if (!invoice?.InvoiceID || !isModuleEnabled('purchase_orders')) return;
    setPoLoading(true);
    purchaseOrdersAPI.getForInvoice(invoice.InvoiceID)
      .then(r => setAvailablePOs(r.data?.pos || []))
      .catch(() => setAvailablePOs([]))
      .finally(() => setPoLoading(false));
  }, [invoice?.InvoiceID]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecalculateCurrency = async (useManualRate = false) => {
    try {
      setRecalculating(true);
      const body = {};
      if (useManualRate && recalcRate) {
        body.rate = parseFloat(recalcRate);
        if (isNaN(body.rate) || body.rate <= 0) {
          alert('Please enter a valid positive exchange rate.');
          return;
        }
      } else {
        body.fetchLatest = true;
      }
      const response = await invoicesAPI.recalculateCurrency(id, body);
      alert(`${response.data.message}\nRate: ${parseFloat(response.data.exchangeRate).toFixed(6)} (${response.data.rateSource})\nConverted: ${response.data.convertedCurrency} ${response.data.convertedTotal.toFixed(2)}`);
      setShowRecalcPanel(false);
      setRecalcRate('');
      await fetchInvoice(); // Refresh
    } catch (error) {
      console.error('Recalculate error:', error);
      const msg = error.response?.data?.message || 'Failed to recalculate currency';
      alert(msg);
    } finally {
      setRecalculating(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getById(id);
      setInvoice(response.data.invoice);
      setLines(response.data.lines || []);
      setApprovals(response.data.approvals || []);
      setEditedInvoice(response.data.invoice);
      setEditedLines(response.data.lines || []);
      
      // Fetch duplicates if this is a duplicate invoice
      if (response.data.invoice.IsDuplicate === 1) {
        await fetchDuplicates(response.data.invoice);
      }
    } catch (error) {
      console.error('Fetch invoice error:', error);
      alert('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await currenciesAPI.getAll();
      console.log('Currencies API response:', response);
      // Handle both response.data and direct array response
      const currencyList = Array.isArray(response) ? response : (response.data?.currencies || response.data || []);
      console.log('Setting currencies:', currencyList);
      setCurrencies(currencyList);
    } catch (error) {
      console.error('Fetch currencies error:', error);
      // Set empty array on error
      setCurrencies([]);
    }
  };

  const fetchVendors = async () => {
    try {
      let response;
      if (isDeptManager) {
        response = await apiClient.get('/vendors/my-department');
        const vendorList = response.data.vendors || response.data || [];
        setVendors(vendorList);
      } else {
        response = await vendorsAPI.getAll();
        console.log('Vendors API response:', response);
        const vendorList = Array.isArray(response) ? response : (response.data?.vendors || response.data || []);
        console.log('Setting vendors:', vendorList);
        setVendors(vendorList);
      }
    } catch (error) {
      console.error('Fetch vendors error:', error);
      setVendors([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/departments');
      const deptList = Array.isArray(response.data?.departments) 
        ? response.data.departments 
        : (Array.isArray(response.data) ? response.data : []);
      setDepartments(deptList);
    } catch (error) {
      console.error('Fetch departments error:', error);
      setDepartments([]);
    }
  };

  const fetchSplitChildren = async () => {
    try {
      const response = await apiClient.get(`/invoice-splits/${id}/children`);
      setSplitChildren(response.data.children || []);
      setSplitInfo(response.data.split || null);
    } catch {
      setSplitChildren([]);
      setSplitInfo(null);
    }
  };

  const handleSplitInvoice = async ({ splitType, allocations }) => {
    await apiClient.post(`/invoice-splits/${id}/split`, { splitType, allocations });
    setShowSplitModal(false);
    await fetchInvoice();
    await fetchSplitChildren();
    alert('Invoice split successfully! Child invoices created.');
  };

  const handleRollbackSplit = async () => {
    if (!window.confirm(`Rollback split on ${invoice.InvoiceNumber}?\n\nThis will delete all child invoices and restore this invoice to Draft.`)) return;
    try {
      await apiClient.post(`/invoice-splits/${id}/rollback`);
      await fetchInvoice();
      await fetchSplitChildren();
      alert('Split rolled back. Invoice restored to Draft.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rollback split');
    }
  };

  const fetchDuplicates = async (currentInvoice) => {
    try {
      const response = await invoicesAPI.getAll({
        showDuplicates: true
      });
      const allInvoices = response.data?.items || response.data?.invoices || [];
      
      // Filter to only invoices with same InvoiceNumber and VendorID
      const sameDuplicates = allInvoices.filter(inv => 
        inv.InvoiceNumber === currentInvoice.InvoiceNumber &&
        inv.VendorID === currentInvoice.VendorID
      );
      
      setDuplicates(sameDuplicates);
      
      // Find current invoice's position in the duplicates array
      const currentIndex = sameDuplicates.findIndex(inv => inv.InvoiceID === currentInvoice.InvoiceID);
      setCurrentDuplicateIndex(currentIndex >= 0 ? currentIndex : 0);
    } catch (error) {
      console.error('Fetch duplicates error:', error);
    }
  };

  const navigateToDuplicate = (direction) => {
    const newIndex = currentDuplicateIndex + direction;
    if (newIndex >= 0 && newIndex < duplicates.length) {
      const targetInvoice = duplicates[newIndex];
      navigate(`/invoices/${targetInvoice.InvoiceID}`);
    }
  };

  const handleSubmitForApproval = async () => {
    if (invoice.IsDuplicate === 1) {
      alert(`❌ Cannot submit — this invoice is flagged as a duplicate.\n\nPlease resolve the duplicate before submitting for approval.`);
      return;
    }

    if (!editedInvoice.DepartmentID) {
      alert('Please assign a department before submitting for approval');
      return;
    }

    if (!window.confirm(`Submit invoice ${invoice.InvoiceNumber} for approval?`)) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiClient.post(`/invoices/${id}/submit`);
      const isAutoApproved = response.data.status === 'Approved';
      if (isAutoApproved) {
        alert('Invoice auto-approved — no approvers are configured for this department.');
      } else {
        const levels = response.data.approvals?.length || 1;
        alert(`Invoice submitted for approval (${levels} approval${levels !== 1 ? 's' : ''} required).`);
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Submit invoice error:', error);
      const errorData = error.response?.data;
      
      // Handle specific validation errors with detailed messages
      if (errorData?.missingFields && errorData.missingFields.length > 0) {
        alert(`❌ Cannot submit - Missing required fields:\n\n${errorData.missingFields.map(f => `• ${f}`).join('\n')}\n\nPlease fill in all required information before submitting.`);
      } else if (errorData?.issues && errorData.issues.length > 0) {
        alert(`❌ Cannot submit - Data validation failed:\n\n${errorData.issues.map(i => `• ${i}`).join('\n')}\n\nPlease correct these mismatches before submitting.`);
      } else if (errorData?.vendorStatus) {
        setVendorError({
          message: errorData.message,
          vendorStatus: errorData.vendorStatus,
          vendorName: errorData.vendorName || invoice?.VendorName || '',
        });
      } else {
        alert(errorData?.message || 'Failed to submit invoice');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForResubmission = async () => {
    if (!window.confirm(`Reset invoice ${invoice.InvoiceNumber} to draft status?\n\nThis will:\n• Delete all approval records\n• Reset status to 'New'\n• Allow resubmission for approval`)) {
      return;
    }

    try {
      setSubmitting(true);
      await apiClient.post(`/invoices/${id}/reset-for-resubmission`);
      alert('Invoice reset successfully. You can now resubmit it.');
      await fetchInvoice();
    } catch (error) {
      console.error('Reset invoice error:', error);
      alert(error.response?.data?.message || 'Failed to reset invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (deleteType) => {
    try {
      await invoicesAPI.delete(invoice.InvoiceID, deleteType);
      setShowDeleteModal(false);
      alert(deleteType === 'hard' ? 'Invoice permanently deleted' : 'Invoice archived successfully');
      navigate('/invoices');
    } catch (error) {
      console.error('Delete invoice error:', error);
      alert(error.response?.data?.message || 'Failed to delete invoice');
      throw error; // re-throw so modal can reset confirming state
    }
  };

  const toggleEditMode = () => {
    if (isEditMode && hasChanges) {
      if (!confirm('You have unsaved changes. Discard them?')) {
        return;
      }
      // Reset to original data
      setEditedInvoice({ ...invoice });
      setEditedLines([...lines]);
      setHasChanges(false);
    }
    setIsEditMode(!isEditMode);
    setSelectedField(null);
  };

  const handleFieldClick = (fieldName, inputRef = null) => {
    if (!isEditMode) return;
    setSelectedField(fieldName);
    if (inputRef) {
      selectedFieldInputRef.current = inputRef;
    }
  };

  const handleFieldChange = (section, field, value, index = null) => {
    // Parse numeric values
    const numericFields = ['SubTotal', 'TaxAmount', 'TotalAmount', 'Quantity', 'UnitPrice', 'Amount', 'LineTotal'];
    let parsedValue = value;
    
    if (numericFields.includes(field) && value !== '') {
      parsedValue = parseFloat(value) || 0;
    }
    
    if (section === 'invoice') {
      setEditedInvoice(prev => ({ ...prev, [field]: parsedValue }));
    } else if (section === 'line') {
      setEditedLines(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: parsedValue };
        
        // Auto-calculate LineTotal when Quantity or UnitPrice changes
        if (field === 'Quantity' || field === 'UnitPrice') {
          const quantity = field === 'Quantity' ? parsedValue : (updated[index].Quantity || 0);
          const unitPrice = field === 'UnitPrice' ? parsedValue : (updated[index].UnitPrice || 0);
          updated[index].LineTotal = quantity * unitPrice;
          updated[index].Amount = updated[index].LineTotal; // Keep Amount in sync
        }
        
        // When LineTotal changes directly, sync Amount
        if (field === 'LineTotal') {
          updated[index].Amount = parsedValue;
        }
        
        // When Amount changes directly, sync LineTotal
        if (field === 'Amount') {
          updated[index].LineTotal = parsedValue;
        }
        
        return updated;
      });
    }
    setHasChanges(true);
  };

  const addNewLine = () => {
    const newLine = {
      InvoiceLineID: `new-${Date.now()}`, // Temporary ID for new lines
      LineNumber: editedLines.length + 1,
      Description: '',
      Quantity: 1,
      UnitPrice: 0,
      Amount: 0,
      LineTotal: 0,
      isNew: true
    };
    setEditedLines(prev => [...prev, newLine]);
    setHasChanges(true);
  };

  const removeLine = (index) => {
    if (confirm('Remove this line item?')) {
      setEditedLines(prev => {
        const updated = prev.filter((_, i) => i !== index);
        // Renumber lines
        return updated.map((line, idx) => ({ ...line, LineNumber: idx + 1 }));
      });
      setHasChanges(true);
    }
  };

  const calculateLinesTotal = () => {
    const linesToUse = isEditMode ? editedLines : lines;
    return linesToUse.reduce((sum, line) => {
      return sum + (parseFloat(line.LineTotal) || parseFloat(line.Amount) || 0);
    }, 0);
  };

  const getSubtotalWarning = () => {
    if (!invoice) return null;
    const linesTotal = calculateLinesTotal();
    const invoiceToCheck = isEditMode ? editedInvoice : invoice;
    // Skip subtotal check for currency-converted invoices — rounding across line items is inevitable
    if (invoice.OriginalCurrency && invoice.ExchangeRate && invoice.ExchangeRate !== 1) return null;
    const subtotal = parseFloat(invoiceToCheck?.SubTotal) || 0;
    const difference = Math.abs(linesTotal - subtotal);
    
    // Allow rounding differences up to 5p (covers floating point & currency conversion artefacts)
    if (difference > 0.05) {
      return {
        show: true,
        linesTotal,
        subtotal,
        difference
      };
    }
    return null;
  };

  const getTotalWarning = () => {
    const invoiceToCheck = isEditMode ? editedInvoice : invoice;
    // Skip total check for currency-converted invoices — each field is converted separately, rounding is inevitable
    if (invoice.OriginalCurrency && invoice.ExchangeRate && invoice.ExchangeRate !== 1) return null;
    const subtotal = parseFloat(invoiceToCheck?.SubTotal) || 0;
    const tax = parseFloat(invoiceToCheck?.TaxAmount) || 0;
    const total = parseFloat(invoiceToCheck?.TotalAmount) || 0;
    const expectedTotal = subtotal + tax;
    const difference = Math.abs(expectedTotal - total);
    
    // Allow rounding differences up to 5p (covers floating point & currency conversion artefacts)
    if (difference > 0.05) {
      return {
        show: true,
        expectedTotal,
        total,
        difference
      };
    }
    return null;
  };

  const getValidationWarnings = () => {
    const warnings = [];
    const invoiceToCheck = isEditMode ? editedInvoice : invoice;
    
    if (!invoiceToCheck.VendorID) {
      warnings.push({ field: 'Vendor', message: 'Vendor is required' });
    }
    if (!invoiceToCheck.InvoiceDate) {
      warnings.push({ field: 'Invoice Date', message: 'Invoice date is required' });
    }
    if (!invoiceToCheck.Currency) {
      warnings.push({ field: 'Currency', message: 'Currency is required' });
    }
    
    return warnings;
  };

  const handlePaste = (e, section, field, index = null) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();
    
    const numericFields = ['SubTotal', 'TaxAmount', 'TotalAmount', 'Quantity', 'UnitPrice', 'Amount', 'LineTotal'];
    
    // Try to parse as number if it's a numeric field
    let value = pastedText;
    if (numericFields.includes(field)) {
      // Remove currency symbols, commas, and whitespace
      const cleanText = pastedText.replace(/[£$€,\s]/g, '').trim();
      const parsed = parseFloat(cleanText);
      if (!isNaN(parsed)) {
        value = parsed;
      }
    }
    
    if (section === 'invoice') {
      setEditedInvoice(prev => ({ ...prev, [field]: value }));
    } else if (section === 'line') {
      setEditedLines(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    }
    
    setHasChanges(true);
    setSelectedField(null);
  };

  const saveChanges = async () => {
    try {
      setSaving(true);
      
      // Prepare update data
      const updateData = {
        InvoiceNumber: editedInvoice.InvoiceNumber,
        VendorID: editedInvoice.VendorID,
        InvoiceDate: editedInvoice.InvoiceDate,
        DueDate: editedInvoice.DueDate,
        SubTotal: parseFloat(editedInvoice.SubTotal) || 0,
        TaxAmount: parseFloat(editedInvoice.TaxAmount) || 0,
        TotalAmount: parseFloat(editedInvoice.TotalAmount) || 0,
        Currency: editedInvoice.Currency,
        OriginalCurrency: editedInvoice.OriginalCurrency || null,
        PaymentTerms: editedInvoice.PaymentTerms,
        Notes: editedInvoice.Notes,
        PurchaseOrder: editedInvoice.PurchaseOrder,
        lines: editedLines.map((line, index) => ({
          InvoiceLineID: line.isNew ? undefined : line.InvoiceLineID, // Undefined for new lines
          LineNumber: index + 1,
          Description: line.Description,
          Quantity: parseFloat(line.Quantity) || 1,
          UnitPrice: parseFloat(line.UnitPrice) || 0,
          Amount: parseFloat(line.Amount) || parseFloat(line.LineTotal) || 0,
          LineTotal: parseFloat(line.LineTotal) || 0,
          // BUGFIX L-1: preserve GL and budget assignments — omitting them caused silent erasure on every save
          GLAccountID: line.GLAccountID || null,
          BudgetLineID: line.BudgetLineID || null,
          isNew: line.isNew || false
        }))
      };

      console.log('Sending update data:', updateData);
      await invoicesAPI.update(id, updateData);
      
      // Refresh data
      await fetchInvoice();
      setIsEditMode(false);
      setHasChanges(false);
      setSelectedField(null);
      alert('Invoice updated successfully!');
      
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save changes: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    return getSymbol(currency);
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // BUGFIX M-8: use UTC methods so a UTC date string like "2024-03-15T00:00:00Z"
    // doesn't shift to the day before in timezones behind UTC (e.g. UTC-5 → 2024-03-14)
    const date = new Date(dateValue);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    return parseFloat(amount).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const renderPOField = () => {
    const hasPO = !!invoice.POID;
    const hasLegacyText = !hasPO && !!displayInvoice.PurchaseOrder;
    const fmtGBP = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: invoice.POCurrency || invoice.Currency || 'GBP' }).format(v || 0);

    if (isEditMode) {
      return (
        <div>
          <label className="text-sm font-medium text-gray-500">Purchase Order</label>
          {poLoading
            ? <p className="text-xs text-gray-400 mt-1">Loading available POs…</p>
            : (
            <select
              value={editedInvoice.PurchaseOrder || ''}
              onChange={e => handleFieldChange('invoice', 'PurchaseOrder', e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">— No PO —</option>
              {availablePOs.map(po => (
                <option key={po.POID} value={po.PONumber}>
                  {po.PONumber} — {po.VendorName} — Remaining: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: po.Currency || 'GBP' }).format(po.RemainingBalance || 0)}
                </option>
              ))}
            </select>
          )}
          {availablePOs.length === 0 && !poLoading && (
            <p className="text-xs text-gray-400 mt-1">No approved POs available for this vendor</p>
          )}
        </div>
      );
    }

    if (hasPO) {
      const remaining = (invoice.POTotalAmount || 0) - (invoice.POMatchedAmount || 0);
      return (
        <div>
          <label className="text-sm font-medium text-gray-500">Purchase Order</label>
          <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-green-800 text-sm">{invoice.PONumber}</p>
                <p className="text-xs text-green-600 mt-0.5">
                  {invoice.POStatus} · Remaining: {fmtGBP(remaining)}
                  {invoice.POSource && invoice.POSource !== 'internal' && ` · ${invoice.POSource === 'dynamics_bc' ? 'Dynamics BC' : 'Xero'}`}
                </p>
              </div>
              <Link to={`/purchase-orders/${invoice.POID}`} className="text-xs text-indigo-600 hover:underline whitespace-nowrap">View PO →</Link>
            </div>
          </div>
        </div>
      );
    }

    if (hasLegacyText) {
      return (
        <div>
          <label className="text-sm font-medium text-gray-500">Purchase Order</label>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-gray-700 text-sm">{displayInvoice.PurchaseOrder}</p>
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">unmatched</span>
          </div>
        </div>
      );
    }

    return (
      <div>
        <label className="text-sm font-medium text-gray-500">Purchase Order</label>
        <p className="text-gray-400 text-sm italic">No PO linked</p>
      </div>
    );
  };

  const renderEditableField = (label, value, fieldName, type = 'text') => {
    const fullFieldName = `invoice.${fieldName}`;
    const isSelected = selectedField === fullFieldName;
    let displayValue = isEditMode ? editedInvoice[fieldName] : value;
    
    // Format dates for date inputs
    if (type === 'date' && displayValue) {
      displayValue = formatDateForInput(displayValue);
    }

    return (
      <div>
        <label className="text-sm font-medium text-gray-500">{label}</label>
        {isEditMode ? (
          <input
            ref={isSelected ? selectedFieldInputRef : null}
            type={type}
            value={displayValue || ''}
            onChange={(e) => handleFieldChange('invoice', fieldName, e.target.value)}
            onPaste={(e) => handlePaste(e, 'invoice', fieldName)}
            onClick={(e) => handleFieldClick(fullFieldName, e.target)}
            className={`mt-1 w-full px-3 py-2 border rounded-md ${
              isSelected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder={isSelected ? 'Paste value here (Ctrl+V)' : 'Click to select'}
          />
        ) : (
          <p className="text-gray-900">{value || 'N/A'}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <SkeletonLoader type="detail" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Invoice not found</p>
        <button onClick={() => navigate('/invoices')} className="btn-secondary mt-4">
          Back to Invoices
        </button>
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(isEditMode ? editedInvoice?.Currency : invoice.Currency);
  const hasDocument = invoice.DocumentUrl;
  const displayInvoice = isEditMode ? editedInvoice : invoice;
  const displayLines = isEditMode ? editedLines : lines;
  const subtotalWarning = getSubtotalWarning();
  const totalWarning = getTotalWarning();
  const validationWarnings = getValidationWarnings();

  return (
    <>
    <div className="w-full h-[calc(100vh-4rem)] flex flex-col bg-gray-50">
      {/* Header - Compact */}
      <div className="flex justify-between items-center px-6 py-3 bg-white border-b shadow-sm shrink-0">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <FileText className="w-7 h-7 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                {displayInvoice.InvoiceNumber || `Invoice ${id.substring(0, 8)}`}
              </h1>
              {/* Vendor approval status badge */}
              {invoice.VendorApprovalStatus === 'Approved' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300" title="Vendor is approved">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Vendor Approved
                </span>
              )}
              {invoice.VendorApprovalStatus === 'Pending' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse" title="Vendor approval is pending">
                  <Clock className="w-3.5 h-3.5" />
                  Vendor Pending
                </span>
              )}
              {invoice.VendorApprovalStatus === 'Rejected' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300" title="Vendor has been rejected">
                  <XCircle className="w-3.5 h-3.5" />
                  Vendor Rejected
                </span>
              )}
              {/* Duplicate badge */}
              {invoice.IsDuplicate === 1 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300" title="This invoice number + vendor combination appears more than once">
                  <Copy className="w-3.5 h-3.5" />
                  Duplicate
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">Created {new Date(invoice.CreatedAt).toLocaleDateString()}</p>
            
            {/* Department Assignment & Submit - Always Visible for New/Draft invoices */}
            {(invoice.Status === 'New' || invoice.Status === 'Draft') && (
              <div className="mt-4 flex items-end gap-4">
                <div className="flex-1 max-w-sm">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    📋 Assign Department <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={editedInvoice?.DepartmentID || ''}
                    onChange={(e) => {
                      const deptId = e.target.value;
                      setEditedInvoice(prev => ({ ...prev, DepartmentID: deptId }));
                      // Auto-save department assignment
                      if (deptId) {
                        // Send all invoice data with the department update
                        const updateData = {
                          InvoiceNumber: editedInvoice.InvoiceNumber || invoice.InvoiceNumber,
                          VendorID: editedInvoice.VendorID || invoice.VendorID,
                          InvoiceDate: editedInvoice.InvoiceDate || invoice.InvoiceDate,
                          DueDate: editedInvoice.DueDate || invoice.DueDate,
                          SubTotal: editedInvoice.SubTotal || invoice.SubTotal || 0,
                          TaxAmount: editedInvoice.TaxAmount || invoice.TaxAmount || 0,
                          TotalAmount: editedInvoice.TotalAmount || invoice.TotalAmount || 0,
                          Currency: editedInvoice.Currency || invoice.Currency,
                          PaymentTerms: editedInvoice.PaymentTerms || invoice.PaymentTerms,
                          Notes: editedInvoice.Notes || invoice.Notes,
                          PurchaseOrder: editedInvoice.PurchaseOrder || invoice.PurchaseOrder,
                          DepartmentID: deptId,
                          lines: lines
                        };
                        
                        apiClient.put(`/invoices/${id}`, updateData)
                          .then(() => {
                            console.log('Department assigned successfully');
                            fetchInvoice(); // Refresh to get department name
                          })
                          .catch(err => {
                            console.error('Failed to assign department:', err);
                            alert('Failed to assign department');
                          });
                      }
                    }}
                    className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium ${
                      !editedInvoice?.DepartmentID 
                        ? 'border-orange-400 bg-orange-50 text-orange-900' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">Select Department...</option>
                    {Array.isArray(departments) && departments.map(dept => (
                      <option key={dept.DepartmentID} value={dept.DepartmentID}>
                        {dept.DepartmentName || dept.Name}
                      </option>
                    ))}
                  </select>
                  {!editedInvoice?.DepartmentID && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">
                      ⚠️ Required before submission
                    </p>
                  )}
                </div>
                
                {/* Submit for Approval Button */}
                {editedInvoice?.DepartmentID && (
                  <button
                    onClick={handleSubmitForApproval}
                    disabled={submitting}
                    className="btn-success shadow-md hover:shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        ✅ Submit for Approval
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Duplicate Navigation */}
            {invoice.IsDuplicate === 1 && duplicates.length > 0 && (
              <div className="mt-2 flex items-center gap-3 p-2 bg-orange-50 border border-orange-200 rounded-md">
                <span className="text-sm text-orange-800 font-medium">
                  Duplicate {currentDuplicateIndex + 1} of {duplicates.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigateToDuplicate(-1)}
                    disabled={currentDuplicateIndex === 0}
                    className="p-1 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous duplicate"
                  >
                    <ChevronLeft className="w-4 h-4 text-orange-700" />
                  </button>
                  <button
                    onClick={() => navigateToDuplicate(1)}
                    disabled={currentDuplicateIndex === duplicates.length - 1}
                    className="p-1 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next duplicate"
                  >
                    <ChevronRight className="w-4 h-4 text-orange-700" />
                  </button>
                </div>
              </div>
            )}
            {isEditMode && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-800 text-sm font-medium">
                  💡 <strong>How to edit:</strong> Click a field → Select text in PDF → Copy (Ctrl+C) → Paste in field (Ctrl+V)
                </p>
                {selectedField && (
                  <p className="text-blue-600 text-sm mt-1">
                    ✏️ Selected: <strong>{selectedField.split('.')[1]}</strong> - Ready to paste!
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <span className={`badge ${getStatusBadgeClass(invoice.Status)}`}>
              {invoice.Status}
            </span>
            {!isEditMode ? (
              <>
                {hasPermission('delete_invoices') && (
                  <button 
                    onClick={() => setShowDeleteModal(true)}
                    className="btn-danger flex items-center gap-2"
                    title="Delete invoice"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
                
                {/* Reset for Resubmission button - Finance Admin only, for submitted/approved invoices */}
                {hasPermission('submit_invoices') && 
                 invoice.Status && 
                 ['Pending Approval', 'Approved', 'Rejected'].includes(invoice.Status) && (
                  <button
                    onClick={handleResetForResubmission}
                    disabled={submitting}
                    className="btn-warning shadow-md hover:shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Resetting...
                      </>
                    ) : (
                      <>
                        🔄 Reset for Resubmission
                      </>
                    )}
                  </button>
                )}
                
                <button 
                  onClick={toggleEditMode}
                  className="btn-primary"
                >
                  ✏️ Edit Invoice
                </button>
                {/* Split Invoice button - for Draft/New/Pending that aren't children or already split */}
                {hasPermission('edit_invoices') && 
                 ['New', 'Draft', 'Pending'].includes(invoice.Status) && 
                 !invoice.ParentInvoiceID && 
                 invoice.SplitStatus !== 'Split' && 
                 lines.length > 0 && (
                  <button
                    onClick={() => setShowSplitModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    <Scissors className="w-4 h-4" />
                    Split Invoice
                  </button>
                )}
                {/* Rollback Split button - only for split parents */}
                {hasPermission('edit_invoices') && invoice.SplitStatus === 'Split' && (
                  <button
                    onClick={handleRollbackSplit}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    <Undo2 className="w-4 h-4" />
                    Rollback Split
                  </button>
                )}
                {hasDocument && (
                  <button 
                    onClick={() => setShowDocument(!showDocument)}
                    className="btn-secondary"
                  >
                    {showDocument ? '📄 Hide Document' : '📄 Show Document'}
                  </button>
                )}
              </>
            ) : (
              <>
                <button 
                  onClick={saveChanges}
                  disabled={!hasChanges || saving}
                  className="btn-primary"
                >
                  {saving ? '💾 Saving...' : '💾 Save Changes'}
                </button>
                <button 
                  onClick={toggleEditMode}
                  disabled={saving}
                  className="btn-secondary"
                >
                  ❌ Cancel
                </button>
              </>
            )}
            
            <button onClick={() => navigate('/invoices')} className="btn-secondary">
              ← Back to List
            </button>
          </div>
        </div>
      </div>

      {/* Split Screen Layout */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Side - Invoice Details */}
        <div className={`${showDocument && hasDocument ? 'w-1/2' : 'w-full'} overflow-y-auto px-6 py-4`}>
          <div className="max-w-5xl space-y-4">

          {/* Workflow Progress Timeline */}
          <DocumentProgressTimeline
            status={invoice.Status}
            approvals={approvals}
            documentType={invoice.DocumentType === 'CreditNote' ? 'CreditNote' : 'Invoice'}
          />

          {/* Split Parent Banner - shown when invoice is split */}
          {invoice.SplitStatus === 'Split' && splitChildren.length > 0 && (
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-indigo-900">This invoice has been split into {splitChildren.length} parts</p>
                  <div className="mt-2 space-y-1">
                    {splitChildren.map(child => (
                      <div key={child.InvoiceID} className="flex items-center gap-3 text-sm">
                        <button
                          onClick={() => navigate(`/invoices/${child.InvoiceID}`)}
                          className="text-indigo-700 hover:text-indigo-900 font-medium underline"
                        >
                          {child.InvoiceNumber}
                        </button>
                        <span className="text-gray-600">{child.DepartmentName}</span>
                        <span className="font-mono text-gray-700">{currencySymbol}{formatAmount(child.TotalAmount)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          child.Status === 'Approved' ? 'bg-green-100 text-green-700' :
                          child.Status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                          child.Status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {child.Status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Split Child Banner - shown on child invoices */}
          {invoice.ParentInvoiceID && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-3">
                <GitBranch className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-semibold text-purple-900">
                    Split Part {invoice.SplitIndex} &mdash; This is a child of a split invoice
                  </p>
                  <button
                    onClick={() => navigate(`/invoices/${invoice.ParentInvoiceID}`)}
                    className="text-sm text-purple-700 hover:text-purple-900 underline mt-1"
                  >
                    View Parent Invoice →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Consolidated Banner */}
          {invoice.SplitStatus === 'Consolidated' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <GitBranch className="w-5 h-5 text-green-600" />
                <p className="font-semibold text-green-900">
                  All split parts have been approved. Invoice consolidated.
                </p>
              </div>
            </div>
          )}
          
          {/* Invoice Information */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Invoice Information
              {isEditMode && <span className="text-sm text-blue-600 ml-2">(Click field, then select text from PDF)</span>}
            </h2>
            
            {/* Validation Warnings */}
            {validationWarnings.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 text-xl">⚠️</span>
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">Missing Required Fields</p>
                    <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                      {validationWarnings.map((warning, idx) => (
                        <li key={idx}>{warning.field}: {warning.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderEditableField('Invoice Number', displayInvoice.InvoiceNumber, 'InvoiceNumber')}
              
              {/* Vendor Dropdown */}
              <div>
                <label className="text-sm font-medium text-gray-500">Vendor</label>
                {isEditMode ? (
                  <>
                  <select
                    value={editedInvoice.VendorID || ''}
                    onChange={(e) => handleFieldChange('invoice', 'VendorID', e.target.value)}
                    className={`mt-1 w-full px-3 py-2 border rounded-md ${
                      !editedInvoice.VendorID ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Vendor...</option>
                    {vendors.map(vendor => (
                      <option key={vendor.VendorID} value={vendor.VendorID}>
                        {vendor.VendorName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setAddVendorError(''); setShowAddVendorModal(true); }}
                    className="mt-1.5 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <PlusCircle className="w-3.5 h-3.5" />
                    Add new vendor
                  </button>
                  </>
                ) : (
                  <div>
                    <p className="text-gray-900">
                      {displayInvoice.VendorName || 
                        <span className="text-red-600">⚠️ Not Set</span>
                      }
                    </p>
                    {invoice.VendorApprovalStatus === 'Pending' && (
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded bg-orange-100 text-orange-800 border border-orange-300">
                        <AlertTriangle className="w-4 h-4" />
                        <span>This vendor is pending approval</span>
                      </div>
                    )}
                    {invoice.VendorApprovalStatus === 'Rejected' && (
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded bg-red-100 text-red-800 border border-red-300">
                        <AlertTriangle className="w-4 h-4" />
                        <span>⚠️ This vendor has been rejected and should not be used</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Department - Display Only */}
              <div>
                <label className="text-sm font-medium text-gray-500">Department</label>
                <p className="text-gray-900">{invoice.DepartmentName || 'Not Assigned'}</p>
              </div>
              
              {renderEditableField('Invoice Date', 
                isEditMode ? editedInvoice.InvoiceDate : (invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString() : 'N/A'), 
                'InvoiceDate', 'date')}
              {renderEditableField('Due Date', 
                isEditMode ? editedInvoice.DueDate : (invoice.DueDate ? new Date(invoice.DueDate).toLocaleDateString() : 'N/A'), 
                'DueDate', 'date')}
              
              {/* Currency Dropdown */}
              <div>
                <label className="text-sm font-medium text-gray-500">Currency</label>
                {isEditMode ? (
                  <>
                  <select
                    value={editedInvoice.Currency || ''}
                    onChange={(e) => handleFieldChange('invoice', 'Currency', e.target.value)}
                    className={`mt-1 w-full px-3 py-2 border rounded-md ${
                      !editedInvoice.Currency ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Currency...</option>
                    {currencies.map(curr => (
                      <option key={curr.CurrencyCode} value={curr.CurrencyCode}>
                        {curr.Symbol} {curr.CurrencyCode} - {curr.CurrencyName}
                      </option>
                    ))}
                  </select>
                  {editedInvoice.Currency && editedInvoice.Currency !== (invoice.DefaultCurrency || 'GBP') && (
                    <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      💱 Save changes — you'll then be able to apply an exchange rate and add this to rate reconciliation.
                    </p>
                  )}
                  </>
                ) : (
                  <p className="text-gray-900">
                    {displayInvoice.Currency ? 
                      `${getCurrencySymbol(displayInvoice.Currency)} ${displayInvoice.Currency}` : 
                      <span className="text-red-600">⚠️ Not Set</span>
                    }
                  </p>
                )}
              </div>
              
              {/* Invoice Received In (Original Currency) */}
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Invoice Received In
                  {!isEditMode && invoice.OriginalCurrency && (
                    <span className="ml-1 text-xs text-blue-600">(original currency)</span>
                  )}
                </label>
                {isEditMode ? (
                  <>
                  <select
                    value={editedInvoice.OriginalCurrency || ''}
                    onChange={(e) => handleFieldChange('invoice', 'OriginalCurrency', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Same as company currency —</option>
                    {currencies.map(curr => (
                      <option key={curr.CurrencyCode} value={curr.CurrencyCode}>
                        {curr.Symbol} {curr.CurrencyCode} - {curr.CurrencyName}
                      </option>
                    ))}
                  </select>
                  {editedInvoice.OriginalCurrency && editedInvoice.OriginalCurrency !== (invoice.DefaultCurrency || 'GBP') && (
                    <p className="mt-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                      💱 After saving, apply an exchange rate to convert to {invoice.DefaultCurrency || 'GBP'} — the invoice will then appear in Rate Reconciliation.
                    </p>
                  )}
                  </>
                ) : (
                  <p className="text-gray-900">
                    {invoice.OriginalCurrency
                      ? `${getCurrencySymbol(invoice.OriginalCurrency)} ${invoice.OriginalCurrency}`
                      : <span className="text-gray-400 text-sm">— same as company currency —</span>
                    }
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Created By</label>
                <p className="text-gray-900">{invoice.CreatedByUsername || 'Unknown'}</p>
              </div>
              
              {isModuleEnabled('purchase_orders')
                ? renderPOField()
                : renderEditableField('Purchase Order', displayInvoice.PurchaseOrder, 'PurchaseOrder')
              }
            </div>
          </div>

          {/* Amounts Breakdown */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Amounts</h2>
            
            {/* Foreign currency without exchange rate warning */}
            {!isEditMode && invoice.OriginalCurrency && invoice.Currency !== invoice.OriginalCurrency && invoice.ExchangeRateConfirmed === false && (!invoice.ExchangeRate || invoice.ExchangeRate === 1) && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 text-xl">💱</span>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">Foreign Currency - Conversion Pending</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This invoice is in <strong>{invoice.OriginalCurrency}</strong> but your company currency is <strong>{invoice.DefaultCurrency || 'GBP'}</strong>.
                      No exchange rate was found. Amounts are shown in the original currency. 
                    </p>
                    <button
                      onClick={() => setShowRecalcPanel(!showRecalcPanel)}
                      className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded font-medium"
                    >
                      💱 Convert to {invoice.DefaultCurrency || 'GBP'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Foreign currency with auto-conversion notice */}
            {!isEditMode && invoice.OriginalCurrency && invoice.Currency !== invoice.OriginalCurrency && invoice.ExchangeRate && invoice.ExchangeRate !== 1 && invoice.ExchangeRateConfirmed === false && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-300 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 text-xl">💱</span>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-800">Auto-Converted from {invoice.OriginalCurrency}</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Original: {getCurrencySymbol(invoice.OriginalCurrency)}{formatAmount(invoice.OriginalAmount)} {invoice.OriginalCurrency} → 
                      Converted at rate {parseFloat(invoice.ExchangeRate).toFixed(4)} to {invoice.Currency}.
                      Please verify the exchange rate is correct.
                    </p>
                    <button
                      onClick={() => setShowRecalcPanel(!showRecalcPanel)}
                      className="mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium"
                    >
                      🔄 Recalculate with Different Rate
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Currency manually set to foreign currency but OriginalCurrency never populated (e.g. OCR got currency wrong) */}
            {!isEditMode && !invoice.OriginalCurrency && invoice.Currency && invoice.Currency !== (invoice.DefaultCurrency || 'GBP') && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 text-xl">💱</span>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">Invoice in Foreign Currency</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This invoice is recorded in <strong>{invoice.Currency}</strong> but your company currency is <strong>{invoice.DefaultCurrency || 'GBP'}</strong>.
                      Apply an exchange rate to convert and add this invoice to rate reconciliation.
                    </p>
                    <button
                      onClick={() => setShowRecalcPanel(!showRecalcPanel)}
                      className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded font-medium"
                    >
                      💱 Convert to {invoice.DefaultCurrency || 'GBP'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Currency where invoice is in different currency but may need recalculation */}
            {!isEditMode && invoice.OriginalCurrency && invoice.Currency === invoice.OriginalCurrency && invoice.OriginalCurrency !== (invoice.DefaultCurrency || 'GBP') && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 text-xl">💱</span>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">Invoice in Foreign Currency</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This invoice is in <strong>{invoice.OriginalCurrency}</strong> and needs to be converted to <strong>{invoice.DefaultCurrency || 'GBP'}</strong>.
                    </p>
                    <button
                      onClick={() => setShowRecalcPanel(!showRecalcPanel)}
                      className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded font-medium"
                    >
                      💱 Convert to {invoice.DefaultCurrency || 'GBP'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Recalculate Panel */}
            {showRecalcPanel && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-300 rounded-md">
                <h3 className="font-semibold text-gray-900 mb-3">Currency Conversion</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Convert <strong>{invoice.OriginalCurrency || invoice.Currency}</strong> {getCurrencySymbol(invoice.OriginalCurrency || invoice.Currency)}{formatAmount(invoice.OriginalAmount || invoice.TotalAmount)} → <strong>{invoice.DefaultCurrency || 'GBP'}</strong>
                </p>
                <div className="flex flex-col gap-3">
                  {/* Auto-fetch option */}
                  <button
                    onClick={() => handleRecalculateCurrency(false)}
                    disabled={recalculating}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium text-sm"
                  >
                    {recalculating ? '⏳ Fetching rate...' : '🌐 Auto-Fetch ECB Rate & Convert'}
                  </button>
                  
                  {/* Manual rate option */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 whitespace-nowrap">Manual Rate:</label>
                    <input
                      type="number"
                      step="0.000001"
                      min="0"
                      value={recalcRate}
                      onChange={(e) => setRecalcRate(e.target.value)}
                      placeholder={`1 ${invoice.OriginalCurrency || invoice.Currency} = ? ${invoice.DefaultCurrency || 'GBP'}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                    <button
                      onClick={() => handleRecalculateCurrency(true)}
                      disabled={recalculating || !recalcRate}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm whitespace-nowrap"
                    >
                      Apply Rate
                    </button>
                  </div>
                  
                  {recalcRate && parseFloat(recalcRate) > 0 && (
                    <p className="text-xs text-gray-500">
                      Preview: {getCurrencySymbol(invoice.OriginalCurrency || invoice.Currency)}{formatAmount(invoice.OriginalAmount || invoice.TotalAmount)} × {recalcRate} = {getCurrencySymbol(invoice.DefaultCurrency || 'GBP')}{formatAmount(parseFloat(invoice.OriginalAmount || invoice.TotalAmount) * parseFloat(recalcRate))}
                    </p>
                  )}

                  <button
                    onClick={() => { setShowRecalcPanel(false); setRecalcRate(''); }}
                    className="text-sm text-gray-500 hover:text-gray-700 underline self-start"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Warning for mismatched totals */}
            {(subtotalWarning || totalWarning) && (
              <div className="mb-4 p-3 bg-red-50 border-2 border-red-400 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 text-xl">🚩</span>
                  <div className="flex-1">
                    <p className="font-bold text-red-900">Validation Errors - Cannot Submit</p>
                    {subtotalWarning && (
                      <p className="text-sm text-red-800 mt-2">
                        ❌ <strong>Subtotal Mismatch:</strong><br/>
                        • Line items total: {currencySymbol}{formatAmount(subtotalWarning.linesTotal)}<br/>
                        • OCR Subtotal: {currencySymbol}{formatAmount(subtotalWarning.subtotal)}<br/>
                        • <span className="font-bold">Difference: {currencySymbol}{formatAmount(subtotalWarning.difference)}</span>
                      </p>
                    )}
                    {totalWarning && (
                      <p className="text-sm text-red-800 mt-2">
                        ❌ <strong>Total Calculation Mismatch:</strong><br/>
                        • Subtotal + Tax = {currencySymbol}{formatAmount(totalWarning.expectedTotal)}<br/>
                        • OCR Total: {currencySymbol}{formatAmount(totalWarning.total)}<br/>
                        • <span className="font-bold">Difference: {currencySymbol}{formatAmount(totalWarning.difference)}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Subtotal:</span>
                {isEditMode ? (
                  <input
                    ref={selectedField === 'invoice.SubTotal' ? selectedFieldInputRef : null}
                    type="number"
                    step="0.01"
                    value={editedInvoice.SubTotal || ''}
                    onChange={(e) => handleFieldChange('invoice', 'SubTotal', e.target.value)}
                    onPaste={(e) => handlePaste(e, 'invoice', 'SubTotal')}
                    onClick={(e) => handleFieldClick('invoice.SubTotal', e.target)}
                    className={`w-32 px-2 py-1 border-2 rounded text-right ${
                      subtotalWarning 
                        ? 'border-red-500 bg-red-50 text-red-900' 
                        : (selectedField === 'invoice.SubTotal' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300')
                    }`}
                    placeholder={selectedField === 'invoice.SubTotal' ? 'Paste here' : '0.00'}
                  />
                ) : (
                  <span className={`font-medium ${
                    subtotalWarning ? 'text-red-700 font-bold' : ''
                  }`}>
                    {currencySymbol}{formatAmount(displayInvoice.SubTotal)}
                    {subtotalWarning && <span className="ml-2 text-red-600">⚠️</span>}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Tax:</span>
                {isEditMode ? (
                  <input
                    ref={selectedField === 'invoice.TaxAmount' ? selectedFieldInputRef : null}
                    type="number"
                    step="0.01"
                    value={editedInvoice.TaxAmount || ''}
                    onChange={(e) => handleFieldChange('invoice', 'TaxAmount', e.target.value)}
                    onPaste={(e) => handlePaste(e, 'invoice', 'TaxAmount')}
                    onClick={(e) => handleFieldClick('invoice.TaxAmount', e.target)}
                    className={`w-32 px-2 py-1 border-2 rounded text-right ${
                      totalWarning 
                        ? 'border-red-500 bg-red-50 text-red-900' 
                        : (selectedField === 'invoice.TaxAmount' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300')
                    }`}
                    placeholder={selectedField === 'invoice.TaxAmount' ? 'Paste here' : '0.00'}
                  />
                ) : (
                  <span className={`font-medium ${
                    totalWarning ? 'text-red-700 font-bold' : ''
                  }`}>
                    {currencySymbol}{formatAmount(displayInvoice.TaxAmount)}
                    {totalWarning && <span className="ml-2 text-red-600">⚠️</span>}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center py-3 border-t-2 border-gray-300">
                <span className="text-lg font-semibold">Total Amount:</span>
                {isEditMode ? (
                  <input
                    ref={selectedField === 'invoice.TotalAmount' ? selectedFieldInputRef : null}
                    type="number"
                    step="0.01"
                    value={editedInvoice.TotalAmount || ''}
                    onChange={(e) => handleFieldChange('invoice', 'TotalAmount', e.target.value)}
                    onPaste={(e) => handlePaste(e, 'invoice', 'TotalAmount')}
                    onClick={(e) => handleFieldClick('invoice.TotalAmount', e.target)}
                    className={`w-40 px-3 py-2 border-2 rounded text-right text-xl font-bold ${
                      totalWarning 
                        ? 'border-red-500 bg-red-50 text-red-900' 
                        : (selectedField === 'invoice.TotalAmount' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300')
                    }`}
                    placeholder={selectedField === 'invoice.TotalAmount' ? 'Paste here' : '0.00'}
                  />
                ) : (
                  <div className={`text-2xl font-bold ${
                    totalWarning ? 'text-red-700' : 'text-blue-600'
                  }`}>
                    <CurrencyDisplay
                      displayAmount={displayInvoice.DisplayAmount || displayInvoice.TotalAmount}
                      defaultCurrency={displayInvoice.Currency}
                      originalAmount={displayInvoice.OriginalAmount}
                      originalCurrency={displayInvoice.OriginalCurrency}
                      exchangeRate={displayInvoice.ExchangeRate}
                    />
                    {totalWarning && <span className="ml-2 text-red-600">⚠️</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Line Items ({displayLines.length})
              </h2>
              {isEditMode && (
                <button
                  onClick={addNewLine}
                  className="btn-success btn-sm"
                >
                  + Add Line Item
                </button>
              )}
            </div>
            {displayLines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-th">Line #</th>
                      <th className="table-th">Description</th>
                      <th className="table-th text-right">Quantity</th>
                      <th className="table-th text-right">Unit Price</th>
                      <th className="table-th text-right">Amount</th>
                      {isEditMode && <th className="table-th">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayLines.map((line, index) => {
                      const descField = `line.Description.${index}`;
                      const qtyField = `line.Quantity.${index}`;
                      const priceField = `line.UnitPrice.${index}`;
                      const totalField = `line.LineTotal.${index}`;
                      
                      return (
                      <tr key={line.InvoiceLineID}>
                        <td className="table-td">{line.LineNumber}</td>
                        <td className="table-td">
                          {isEditMode ? (
                            <input
                              ref={selectedField === descField ? selectedFieldInputRef : null}
                              type="text"
                              value={line.Description || ''}
                              onChange={(e) => handleFieldChange('line', 'Description', e.target.value, index)}
                              onPaste={(e) => handlePaste(e, 'line', 'Description', index)}
                              onClick={(e) => handleFieldClick(descField, e.target)}
                              className={`w-full px-2 py-1 border rounded ${
                                selectedField === descField ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                              }`}
                              placeholder={selectedField === descField ? 'Paste here' : ''}
                            />
                          ) : (
                            line.Description
                          )}
                        </td>
                        <td className="table-td text-right">
                          {isEditMode ? (
                            <input
                              ref={selectedField === qtyField ? selectedFieldInputRef : null}
                              type="number"
                              step="0.01"
                              value={line.Quantity || ''}
                              onChange={(e) => handleFieldChange('line', 'Quantity', e.target.value, index)}
                              onPaste={(e) => handlePaste(e, 'line', 'Quantity', index)}
                              onClick={(e) => handleFieldClick(qtyField, e.target)}
                              className={`w-20 px-2 py-1 border rounded text-right ${
                                selectedField === qtyField ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                              }`}
                              placeholder={selectedField === qtyField ? 'Paste' : '1'}
                            />
                          ) : (
                            line.Quantity || 1
                          )}
                        </td>
                        <td className="table-td text-right">
                          {isEditMode ? (
                            <input
                              ref={selectedField === priceField ? selectedFieldInputRef : null}
                              type="number"
                              step="0.01"
                              value={line.UnitPrice || ''}
                              onChange={(e) => handleFieldChange('line', 'UnitPrice', e.target.value, index)}
                              onPaste={(e) => handlePaste(e, 'line', 'UnitPrice', index)}
                              onClick={(e) => handleFieldClick(priceField, e.target)}
                              className={`w-24 px-2 py-1 border rounded text-right ${
                                selectedField === priceField ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                              }`}
                              placeholder={selectedField === priceField ? 'Paste' : '0.00'}
                            />
                          ) : (
                            `${currencySymbol}${formatAmount(line.UnitPrice)}`
                          )}
                        </td>
                        <td className="table-td text-right font-medium">
                          {isEditMode ? (
                            <input
                              ref={selectedField === totalField ? selectedFieldInputRef : null}
                              type="number"
                              step="0.01"
                              value={line.LineTotal || line.Amount || ''}
                              onChange={(e) => handleFieldChange('line', 'LineTotal', e.target.value, index)}
                              onPaste={(e) => handlePaste(e, 'line', 'LineTotal', index)}
                              onClick={(e) => handleFieldClick(totalField, e.target)}
                              className={`w-28 px-2 py-1 border rounded text-right ${
                                selectedField === totalField ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                              }`}
                              placeholder={selectedField === totalField ? 'Paste' : '0.00'}
                            />
                          ) : (
                            `${currencySymbol}${formatAmount(line.LineTotal || line.Amount)}`
                          )}
                        </td>
                        {isEditMode && (
                          <td className="table-td">
                            <button
                              onClick={() => removeLine(index)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                              title="Remove line"
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="table-td text-right font-semibold">Total:</td>
                      <td className="table-td text-right font-bold text-lg">
                        {currencySymbol}{formatAmount(calculateLinesTotal())}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No line items</p>
            )}
          </div>

          {/* Payment Terms & Notes */}
          {(displayInvoice.PaymentTerms || displayInvoice.Notes || isEditMode) && (
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Additional Information</h2>
              <div className="mb-3">
                <label className="text-sm font-medium text-gray-500">Payment Terms</label>
                {isEditMode ? (
                  <input
                    ref={selectedField === 'invoice.PaymentTerms' ? selectedFieldInputRef : null}
                    type="text"
                    value={editedInvoice.PaymentTerms || ''}
                    onChange={(e) => handleFieldChange('invoice', 'PaymentTerms', e.target.value)}
                    onPaste={(e) => handlePaste(e, 'invoice', 'PaymentTerms')}
                    onClick={(e) => handleFieldClick('invoice.PaymentTerms', e.target)}
                    className={`mt-1 w-full px-3 py-2 border rounded ${
                      selectedField === 'invoice.PaymentTerms' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                    }`}
                    placeholder={selectedField === 'invoice.PaymentTerms' ? 'Paste here' : 'e.g., Net 30'}
                  />
                ) : (
                  <p className="text-gray-900">{displayInvoice.PaymentTerms || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                {isEditMode ? (
                  <textarea
                    ref={selectedField === 'invoice.Notes' ? selectedFieldInputRef : null}
                    value={editedInvoice.Notes || ''}
                    onChange={(e) => handleFieldChange('invoice', 'Notes', e.target.value)}
                    onPaste={(e) => handlePaste(e, 'invoice', 'Notes')}
                    onClick={(e) => handleFieldClick('invoice.Notes', e.target)}
                    rows="4"
                    className={`mt-1 w-full px-3 py-2 border rounded ${
                      selectedField === 'invoice.Notes' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                    }`}
                    placeholder={selectedField === 'invoice.Notes' ? 'Paste here' : 'Additional notes...'}
                  />
                ) : (
                  <p className="text-gray-900 whitespace-pre-wrap">{displayInvoice.Notes || 'N/A'}</p>
                )}
              </div>
            </div>
          )}

          {/* ERP Payment Status */}
          {invoice?.PostedStatus === 'Posted' && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">ERP Payment Status</h2>
                <div className="flex items-center gap-2">
                  {!editingPayment && invoice?.ExternalPostingId && (
                    <button
                      onClick={handleSyncPaymentStatus}
                      disabled={syncingPayment}
                      className="btn btn-secondary btn-sm flex items-center gap-1.5"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingPayment ? 'animate-spin' : ''}`} />
                      {syncingPayment ? 'Syncing…' : 'Sync Status'}
                    </button>
                  )}
                  {!editingPayment ? (
                    <button
                      onClick={handleEditPaymentClick}
                      className="btn btn-secondary btn-sm flex items-center gap-1.5"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSavePaymentStatus}
                        disabled={savingPayment}
                        className="btn btn-primary btn-sm"
                      >
                        {savingPayment ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingPayment(false)}
                        disabled={savingPayment}
                        className="btn btn-secondary btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {editingPayment ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-xs font-medium text-gray-500">ERP Status</label>
                    <select
                      value={paymentForm.ERPPaymentStatus}
                      onChange={e => setPaymentForm(f => ({ ...f, ERPPaymentStatus: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— not set —</option>
                      <option value="Open">Open</option>
                      <option value="Paid">Paid</option>
                      <option value="PartiallyPaid">Partially Paid</option>
                      <option value="Voided">Voided</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Paid At</label>
                    <input
                      type="date"
                      value={paymentForm.ERPPaidAt}
                      onChange={e => setPaymentForm(f => ({ ...f, ERPPaidAt: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Paid Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.ERPPaidAmount}
                      onChange={e => setPaymentForm(f => ({ ...f, ERPPaidAmount: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Payment Ref</label>
                    <input
                      type="text"
                      value={paymentForm.ERPPaymentRef}
                      onChange={e => setPaymentForm(f => ({ ...f, ERPPaymentRef: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Invoice number / ref"
                    />
                  </div>
                </div>
              ) : (
                (() => {
                  const status = erpPayment?.ERPPaymentStatus ?? invoice?.ERPPaymentStatus;
                  const paidAt = erpPayment?.ERPPaidAt ?? invoice?.ERPPaidAt;
                  const paymentRef = erpPayment?.ERPPaymentRef ?? invoice?.ERPPaymentRef;
                  const lastChecked = erpPayment?.ERPPaymentLastChecked ?? invoice?.ERPPaymentLastChecked;
                  const badgeClass =
                    status === 'Paid'          ? 'bg-green-100 text-green-800' :
                    status === 'PartiallyPaid' ? 'bg-yellow-100 text-yellow-800' :
                    status === 'Open'          ? 'bg-gray-100 text-gray-600' :
                    status === 'Voided'        ? 'bg-red-100 text-red-700' :
                                                'bg-gray-100 text-gray-500';
                  return (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="text-xs font-medium text-gray-500">ERP Status</label>
                        <div className="mt-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badgeClass}`}>
                            {status || 'Not set'}
                          </span>
                        </div>
                      </div>
                      {paidAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500">Paid At</label>
                          <p className="mt-1 text-gray-900">{new Date(paidAt).toLocaleDateString()}</p>
                        </div>
                      )}
                      {paymentRef && (
                        <div>
                          <label className="text-xs font-medium text-gray-500">Payment Ref</label>
                          <p className="mt-1 text-gray-900 font-mono text-xs">{paymentRef}</p>
                        </div>
                      )}
                      {lastChecked && (
                        <div>
                          <label className="text-xs font-medium text-gray-500">Last Checked</label>
                          <p className="mt-1 text-gray-400 text-xs">{new Date(lastChecked).toLocaleString()}</p>
                        </div>
                      )}
                      {!status && (
                        <div className="col-span-2 text-xs text-gray-400 italic">
                          No payment status recorded yet. Use Edit to set manually, or Sync Status if linked to an ERP.
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* Applied Credit Notes — shown on regular invoices that have credits */}
          {invoice && invoice.DocumentType !== 'CreditNote' && appliedCredits && appliedCredits.credits?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Applied Credit Notes</h2>
              <div className="space-y-2">
                {appliedCredits.credits.map(cn => (
                  <div key={cn.InvoiceID} className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div>
                      <Link to={`/invoices/${cn.InvoiceID}`} className="font-medium text-teal-800 hover:underline">{cn.InvoiceNumber || 'Credit Note'}</Link>
                      <p className="text-xs text-gray-500 mt-0.5">{cn.InvoiceDate ? new Date(cn.InvoiceDate).toLocaleDateString() : ''} · Status: {cn.Status}</p>
                    </div>
                    <span className="text-sm font-semibold text-teal-700">−{cn.Currency} {parseFloat(cn.TotalAmount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-teal-200 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Net Amount (after credits)</span>
                <span className="text-base font-bold text-gray-900">{appliedCredits.currency} {parseFloat(appliedCredits.netAmount).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Credit Note: link back to parent invoice */}
          {invoice?.DocumentType === 'CreditNote' && invoice?.ParentInvoiceID && (
            <div className="bg-teal-50 rounded-lg border border-teal-200 p-4">
              <h2 className="text-sm font-semibold text-teal-800 mb-1">Credit Note — Applied To</h2>
              <Link to={`/invoices/${invoice.ParentInvoiceID}`} className="text-teal-700 hover:underline font-medium">
                {invoice.ParentInvoiceNumber || 'Original Invoice'}
                {invoice.ParentInvoiceTotalAmount ? ` (${invoice.Currency} ${parseFloat(invoice.ParentInvoiceTotalAmount).toFixed(2)})` : ''}
              </Link>
            </div>
          )}

          {/* Approval History */}
          {approvals.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Approval History</h2>
              <div className="space-y-3">
                {approvals.map((approval) => (
                  <div key={approval.ApprovalID} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{approval.ApproverUsername}</span>
                        <span className="text-sm text-gray-500">({approval.DepartmentName})</span>
                        <span className={`badge ${getApprovalStatusBadgeClass(approval.ApprovalStatus)}`}>
                          {approval.ApprovalStatus}
                        </span>
                      </div>
                      {approval.Comments && (
                        <p className="text-sm text-gray-600 mt-1">{approval.Comments}</p>
                      )}
                      {approval.ApprovedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(approval.ApprovedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Right Side - Document Viewer */}
        {showDocument && hasDocument && (
          <div className="w-1/2 border-l bg-gray-100 flex flex-col">
            <div className="bg-gray-200 px-4 py-2 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-700">
                📄 Invoice Document
                {isEditMode && <span className="text-xs text-blue-600 ml-2">(Select & copy text with Ctrl+C)</span>}
              </h3>
              <a 
                href={invoice.DocumentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                Open in New Tab ↗
              </a>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                ref={iframeRef}
                src={invoice.DocumentUrl}
                className="w-full h-full"
                title="Invoice Document"
              />
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Vendor Error Modal */}
    {vendorError && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Cannot Submit — Vendor Issue</h3>
              <p className="text-gray-600 mt-1 text-sm">{vendorError.message}</p>
            </div>
          </div>

          {vendorError.vendorName && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 mb-5 text-sm">
              <span className="font-medium text-orange-800">Vendor: </span>
              <span className="text-orange-900">{vendorError.vendorName}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {vendorError.vendorStatus === 'Pending' && (
              <Link
                to={`/vendors${vendorError.vendorName ? `?search=${encodeURIComponent(vendorError.vendorName)}` : ''}`}
                onClick={() => setVendorError(null)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
              >
                Go to Vendors →
              </Link>
            )}
            <button
              onClick={() => setVendorError(null)}
              className="flex-1 inline-flex items-center justify-center border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* GL Assignment + Submit Modal for Draft/New invoices */}
    {showGLAssignModal && (
      <InvoiceApprovalDetail
        invoiceId={id}
        onClose={() => setShowGLAssignModal(false)}
        onActionComplete={() => {
          setShowGLAssignModal(false);
          navigate('/invoices');
        }}
      />
    )}

    {/* Delete Invoice Modal */}
    {showDeleteModal && (
      <DeleteInvoiceModal
        invoice={invoice}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />
    )}

    {/* Quick Add Vendor Modal */}
    {showAddVendorModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Add New Vendor</h2>
            <button onClick={() => setShowAddVendorModal(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleQuickAddVendor} className="p-4 space-y-4">
            {addVendorError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                {addVendorError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={addVendorForm.vendorName}
                onChange={(e) => setAddVendorForm(f => ({ ...f, vendorName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. DHL Express UK"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={addVendorForm.contactEmail}
                onChange={(e) => setAddVendorForm(f => ({ ...f, contactEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="accounts@vendor.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input
                type="text"
                value={addVendorForm.contactPhone}
                onChange={(e) => setAddVendorForm(f => ({ ...f, contactPhone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+44 ..."
              />
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              The vendor will be created as <strong>Pending Approval</strong>. It can be approved separately in the Vendors section.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddVendorModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addVendorSaving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {addVendorSaving ? 'Adding...' : 'Add Vendor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Split Invoice Modal */}
    {showSplitModal && (
      <SplitInvoiceModal
        invoice={invoice}
        lines={lines}
        departments={departments}
        currencySymbol={currencySymbol}
        onSplit={handleSplitInvoice}
        onClose={() => setShowSplitModal(false)}
      />
    )}
    </>
  );
}function getStatusBadgeClass(status) {
  const classes = {
    Draft: 'badge-gray',
    Pending: 'badge-yellow',
    Approved: 'badge-green',
    Rejected: 'badge-red',
    Posted: 'badge-blue',
    Split: 'badge-purple'
  };
  return classes[status] || 'badge-gray';
}

function getApprovalStatusBadgeClass(status) {
  const classes = {
    Pending: 'badge-yellow',
    Approved: 'badge-green',
    Rejected: 'badge-red'
  };
  return classes[status] || 'badge-gray';
}
